import React, { useEffect, useState } from 'react';
import { loader } from 'graphql.macro';
import { useMutation, useQuery, useSubscription } from '@apollo/react-hooks';
import GpaCancelPolicy from './gpaCancelPolicy';
import { useLazyQuery } from 'react-apollo';
import GpaPolicyInfo from './gpaPolicyInfo';
import GpaChangePolicy from './gpaChangePolicy';
import { BannerText, WarningText } from '../cancelPolicy/cancelPolicyForm/cancelPolicyFormStyles';
import { ConnectButton, SA360Button } from './gpaCancelPolicyStyles';
import getConfig, { Env } from '../../config';
import AlertLoader from './AlertLoader';
import PolicyInfoBox from './PolicyInfoBox';
import BounceLoaderComponent from './Loader';
import styled from 'styled-components';
import { Box } from 'grommet';
import { ChangePolicy } from '../changePolicy';
import moment from 'moment';
import { formatDateAsString, formatDateObj } from '../../util/formatters';
import useFeatureFlag from '../../util/hooks/useFeatureFlag';
import { DownloadDocumentBanner } from '../cancelPolicy/cancelPolicyForm/formFields/cancelReasonStyles';
import { AltInfo } from '../../icons';

const config = getConfig(process.env.REACT_APP_ENV as Env);

const get_cancel_policy_info = loader(
  './graphql/Get_Cancel_Policy_Info.graphql'
);
const cancel_policy = loader('./graphql/Cancel_Policy.graphql');
const upload_policy_document_mutation = loader(
  './graphql/Cancel_Policy_Upload_Policy_Document.graphql'
);
const on_process_result = loader('./graphql/Run_Subscription_Result.graphql');
const get_effective_date = loader('./graphql/Get_Effective_Date.graphql');
const get_upload_token = loader('./graphql/Get_Upload_Token.graphql');
const get_refund_method_query = loader('./graphql/Get_Refund_Method.graphql');

const StyledLobName = styled.span`
  font-size: 1.5rem;
`;

const StyledUserName = styled.span`
  font-size: 1.2rem;
`;

export interface cancelPolicyProps {
  environment: string;
  sourceSystem: string;
  accountNumber: string;
  policyNumber: string;
}

export default ({
  environment,
  sourceSystem,
  accountNumber,
  policyNumber
}: cancelPolicyProps) => {
  const [isUpdated, setIsUpdated] = useState<Boolean>(false);
  const [startReload, setStartReload] = useState<Boolean>(false);
  const [nCWarning, setNCWarning] = useState(false);
  // const [refundMethods, setRefundMethods] = useState<string[]>([]);
  const { flagDetails: showCancelPolicyFlag } = useFeatureFlag(
    'showCancelPolicyFlag'
  );
  const { flagDetails: showChangePolicyFlag } = useFeatureFlag(
    'showChangePolicyFlag'
  );

  const { data: subscriptionData } = useSubscription(
    on_process_result,
    {
      variables: {
        policy_number: policyNumber,
      },
    }
  );

  if (startReload) {
    window.location.reload();
  };

  let openCancellation = false;
  let source_system_code = '';
  if (sourceSystem === 'commercial') {
    source_system_code = 'GWCL';
  } else if (sourceSystem === 'personal') {
    source_system_code = 'GWPL';
  }

  const [confirmClickHandler, confirmClickHandlerResponse] = useLazyQuery(
    get_cancel_policy_info
  );

  const {
    data: confirmData,
    loading: confirmDataLoading
  } = confirmClickHandlerResponse;
  if (confirmClickHandlerResponse) {
    const confirmResult = confirmData;
  }

  const cancelPolicyInfoResult = useQuery(get_cancel_policy_info, {
    variables: {
      "account_number": accountNumber,
      "directPCCall": true,
      "query_env": environment,
      "policy_number": policyNumber,
      "source_system_code": source_system_code,
    }
  });
  const [uploadDoc, { error }] = useMutation(
    upload_policy_document_mutation, {
    onError: (error: any) => {
      console.error('GraphQL Error:', error);
    }
  }
  );
  const [cancelPolicy, { data: cancelData, error: cancelError }] = useMutation(
    cancel_policy, {
    onError: (error: any) => {
      console.error('GraphQL Error:', error);
    }
  });

  const getEffectDateResult = useQuery(get_effective_date, {
    variables: {
      "policy_number": policyNumber,
      "cancelReasonCode": "fincononpay",
      "cancellationRefundMethod": "prorata",    //TODO: change refund method to be dynamic.
      "cancellationSource": "insured",
      "source_system_code": source_system_code,
      "getEffectiveDate": true,
      "query_env": environment,
      "product": "TEST"
    }
  });
  let effectiveDate;
  const { data: dateResponse } = getEffectDateResult;
  if (dateResponse) {
    effectiveDate = JSON.parse(dateResponse?.getEffectiveDate?.data)?.result;
  }

  const getUploadTokenResult = useQuery(get_upload_token, {
    variables: {
      "source_system_code": source_system_code,
      "getUploadToken": true,
      "query_env": environment,
    }
  });
  let uploadToken = "";
  const { data: uploadTokenResponse } = getUploadTokenResult;
  if (uploadTokenResponse) {
    uploadToken = uploadTokenResponse?.generateUploadToken?.data;
  }

  const [getRefundMethodClickHandler, getRefundMethodQueryResponse] = useLazyQuery(
    get_refund_method_query, { fetchPolicy: 'network-only' }
  );

  let refundMethods = ['Select Refund Method'];
  const { data: refundMethodData } = getRefundMethodQueryResponse;
  if (refundMethodData && refundMethodData?.getRefundData) {
    refundMethods.push(...JSON.parse(refundMethodData?.getRefundData.data));
    // setRefundMethods(JSON.parse(refundMethodData?.getRefundData?.data));
  } else {
    refundMethods.push('prorata');
  }

  const [getAcceptance, acceptanceIndicatorResponse] = useLazyQuery(
    get_cancel_policy_info
  );
  let accResult = null;
  // let filteredPolicyAcc = null;
  let acceptable = false;
  const {
    data: acceptanceData,
    loading: acceptanceLoading
  } = acceptanceIndicatorResponse;
  if (acceptanceIndicatorResponse) {
    // acceptable =
    //   acceptanceData?.account?.items[0]?.policy?.valid_policy_cancellation
    //     ?.cancellationAcceptance;
    accResult = acceptanceData?.valid_policy_cancellation ? JSON.parse(acceptanceData.valid_policy_cancellation.data) : null;
    // status = data?.account?.items[0]?.policy?.status;
    // filteredPolicyAcc = accResult?.result.account.policySummaries.filter((policy: any) => policy.policyNumber == policyNumber);
    acceptable = accResult?.result?.latestPeriod.canCancel;
  }
  const lineOfBusinessMapping = {
    wcLines: ["WC7Line"],
    farmLines: ["FALine", "FULine", "FRMLine"],
    cppLines: ["CP7Line", "GL7Line", "IMLine", "CULine", "CULineCU"],
  };
  // let status = '';
  let code = 'GWCL';
  let isCancelled = false;
  let maxDate = '';
  let minDate = '';
  // let payplans = [];
  let product = '';
  let openChanges = false;
  let result: any = null;
  let scheduledCancellation = false;
  let filteredPolicy = [];
  let isFarmRanchLine = false;
  let lobDisplayName = '';

  const { data: infoData, loading: dataLoading, error: dataError } = cancelPolicyInfoResult;

  useEffect(() => {
    if (dataError && dataError.message.includes("Execution timed out")) {
      console.log('ERROR IN LOADING VALID CANCEL DATA:: ', dataError.message);
    }
  }, [dataError]);

  if (dataError && !dataLoading && !dataError.message.includes("Execution timed out")) {
    return (
      <>
        <WarningText>Data is not available currently, please try again later.</WarningText>
        {/* <ConnectButton onClick={() => { window.open(config.saconnect_url.replace('#env#', "-" + environment)); }} >
          Home
        </ConnectButton> */}
      </>
    );
  }

  // if ( subLoading) {
  //   return (
  //     <>
  //       {subLoading && <AlertLoader message="Please wait..." />}
  //     </>
  //   )
  // }

  // if (dataLoading) {
  //   return (
  //     <>
  //       {dataLoading && <BounceLoaderComponent />}
  //     </>
  //   );
  // }

  if (!subscriptionData) {
    return (
      <>
        {!subscriptionData && <BounceLoaderComponent />}
      </>
    );
  }

  const isDateGreater = (date1: string | Date, date2: string | Date) => {
    date1 = parseDate(date1);
    date2 = parseDate(date2);
    return date1.getTime() > date2.getTime();
  };

  const parseDate = (date: string | Date): Date => {
    if (typeof date === 'string') {
      const d = new Date(date);
      date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    }
    return date;
  };

  let data: any | null | undefined;
  const saconnect_url = config.saconnect_url_emails.replace('#env#', "-" + environment);
  let policyState;
  if (infoData) {
    data = infoData;
    result = data?.valid_policy_cancellation ? JSON.parse(data.valid_policy_cancellation.data) : null;
  }
  if (subscriptionData && !infoData) {
    data = JSON.parse(subscriptionData?.onProcessResult?.message);
    result = JSON.parse(subscriptionData?.onProcessResult?.message);
  }

  if (data && !dataLoading) {
    // status = data?.account?.items[0]?.policy?.status;
    code = data?.account?.items[0]?.source_system_code;
    filteredPolicy = result.result.account?.policySummaries?.filter((policy: any) => policy.policyNumber == policyNumber);
    isCancelled = (!!filteredPolicy && filteredPolicy[0]?.isCancelled || result?.result?.latestPeriod?.displayStatus === 'Canceled');
    scheduledCancellation = result?.result?.scheduledCancellation;
    policyState = result?.result?.account?.accountHolderCompany?.primaryAddress?.state;
    let isCppLine = false;
    // let isFarmRanchLine = false;
    let isWorkerCompLine = false;
    let minCancelationMoment;
    let maxCancelationMoment;
    const policyBusinessLine = !!filteredPolicy && filteredPolicy[0]?.policyLines[0]?.lineOfBusinessCode;
    if (!!policyBusinessLine) {
      if (lineOfBusinessMapping.wcLines.includes(policyBusinessLine)) {
        isWorkerCompLine = true;
      } else if (lineOfBusinessMapping.farmLines.includes(policyBusinessLine)) {
        isFarmRanchLine = true;
      } else if (lineOfBusinessMapping.cppLines.includes(policyBusinessLine)) {
        isCppLine = true;
      }
    }
    const latestPeriodLob = result?.result?.latestPeriod?.policyLines[0]?.lineOfBusinessCode;
    const state = result?.result?.baseState;

    const excludeLobs = ["GL7Line", "IMLine"];
    let periodStart = result?.result?.currentTermStartDate ? formatDateObj(result?.result?.currentTermStartDate) : formatDateObj(result?.result?.latestPeriod?.effectiveDate);
    periodStart = moment.utc(periodStart, "MM/DD/YYYY").format("YYYY-MM-DDTHH:mm:ss");
    minCancelationMoment = moment(periodStart, "YYYY-MM-DDTHH:mm:ss");
    maxCancelationMoment = moment(periodStart, "YYYY-MM-DDTHH:mm:ss").add(1, 'year');
    minDate = minCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
    maxDate = maxCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
    //  || policyBusinessLine === "BP7Line" || policyBusinessLine === "CA7Line" -- call the API
    if ((isCppLine) &&
      !excludeLobs.includes(policyBusinessLine)) {
      minCancelationMoment = moment(periodStart, "YYYY-MM-DDTHH:mm:ss").add(1, 'year');
      maxCancelationMoment = moment(periodStart, "YYYY-MM-DDTHH:mm:ss").add(2, 'year');
      minDate = minCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
      maxDate = maxCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
    }
    if (effectiveDate === undefined) {
      effectiveDate = moment.utc().format("YYYY-MM-DDTHH:mm:ss");
    } else {
      minCancelationMoment = moment(effectiveDate, "YYYY-MM-DDTHH:mm:ss");
      maxCancelationMoment = moment(effectiveDate, "YYYY-MM-DDTHH:mm:ss").add(1, 'year');
      minDate = minCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
      maxDate = maxCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
    }
    effectiveDate = effectiveDate.replace("Z", "");
    if (effectiveDate && isDateGreater(periodStart, effectiveDate)) {
      effectiveDate = periodStart;
    } else if (isCppLine || isWorkerCompLine) {
      effectiveDate = effectiveDate;
      minCancelationMoment = moment(effectiveDate, "YYYY-MM-DDTHH:mm:ss");
      maxCancelationMoment = moment(periodStart, "YYYY-MM-DDTHH:mm:ss").add(1, 'year');
      minDate = minCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
      maxDate = maxCancelationMoment.format("YYYY-MM-DDTHH:mm:ss");
      // } else if (isFarmRanchLine && cancelReasonCode == 'fincononpay') {
      //     effectiveDate = moment.utc().format("YYYY-MM-DDTHH:mm:ss");
    } else {
      effectiveDate = effectiveDate;
    }
    // if(!isCppLine && !isWorkerCompLine && cancelReasonCode === 'nonpayment_Ext') {
    //     var currentDate = moment().add(15, 'day').utc().toDate().toLocalDate();
    //     effectiveDate = currentDate;
    //     minCancelationMoment = moment(currentDate);
    //     minCancelationDate = minCancelationMoment.toDate();
    // }
    // payplans = data?.account?.items[0]?.payplans.items;

    const cppLine = !!filteredPolicy && filteredPolicy[0].policyLines.filter((line: any) => { return line.lineOfBusinessName === 'Commercial Property'; });
    if (!!filteredPolicy && filteredPolicy[0]?.policyLines.length > 1 && !!cppLine) {
      product = cppLine[0]?.lineOfBusinessName;
      lobDisplayName = 'Commercial Package';
    } else {
      product = !!filteredPolicy && filteredPolicy[0]?.policyLines[0]?.lineOfBusinessName;
      lobDisplayName = product;
    }
    if (product === "Workers' Compensation") {
      product = "WORKERS COMP";
    } else if (product === "Commercial Property") {
      product = "COMMERCIAL PACKAGE";
    }
    if (!isWorkerCompLine) {
      openChanges = result?.result?.openPolicyChange;
    }
  }
  if (data && data?.valid_policy_cancellation && data?.valid_policy_cancellation?.data) {
    openCancellation = JSON.parse(data?.valid_policy_cancellation?.data).result?.openCancellation;
  }

  const redirectToSA360 = () => {
    window.location.href = `/account/${accountNumber}/policy/${policyNumber}`;
  };

  return <div>
    <span style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box data-test-id="displayName" pad={{ vertical: 'small' }}>
        <StyledLobName>{lobDisplayName} ({result?.result?.latestPeriod?.policyNumber})</StyledLobName>
        <StyledUserName>{result?.result?.latestPeriod?.primaryInsuredName}</StyledUserName>
      </Box>
      {(environment === 'l4-1' || result?.result?.dbenv === environment) &&
        <Box data-test-id="displayName" pad={{ vertical: 'small' }}>
          <SA360Button onClick={() => redirectToSA360()}>View Policy Details</SA360Button>
        </Box>}
    </span>

    {(result?.result && result?.result?.latestPeriod?.policyLines[0]?.lineOfBusinessCode === 'CULine') && <DownloadDocumentBanner>
      <AltInfo />
      <BannerText>Our team is working on enabling online policy changes for your convenience, meanwhile please send your requests to the email address found in&nbsp;
        <a href={saconnect_url.replace('{state}', policyState)} target="_blank">
          this link
        </a>
        &nbsp;for any changes in your commercial policy.
      </BannerText>
    </DownloadDocumentBanner>}
    {(result?.result && result?.result?.latestPeriod?.policyLines[0].lineOfBusinessCode === 'CULineCU') && <DownloadDocumentBanner>
      <AltInfo />
      <BannerText>Please use the 'Upload Document' section found under 'Change this Policy' tab to submit policy change requests. Should you have any questions, please contact your Underwriter.
      </BannerText>
    </DownloadDocumentBanner>}
    {openChanges && product !== "WORKERS COMP" &&
      (
        <DownloadDocumentBanner>
          <AltInfo />
          <BannerText>There is an open policy change for this policy. Please review incomplete policy change transaction below prior to starting a new change.</BannerText>
        </DownloadDocumentBanner>
      )}

    {!!nCWarning && (
      <DownloadDocumentBanner>
        <AltInfo />
        <BannerText>This cancellation reason requires underwriting review. Please contact your Underwriter to complete this cancellation or select "Other reason for cancellation - Insured's request" to proceed.</BannerText>
      </DownloadDocumentBanner>
    )}

    {result && console.log("Response: ", result)}

    {showCancelPolicyFlag?.enabled && (
      <GpaCancelPolicy
        result={result}
        upload={uploadDoc}
        uploadError={error}
        isCancelled={isCancelled}
        scheduledCancellation={scheduledCancellation}
        cancel={cancelPolicy}
        cancelData={cancelData}
        cancelError={cancelError}
        policyNumber={policyNumber}
        systemCode={source_system_code}
        minDate={minDate}
        maxDate={maxDate}
        product={product}
        getAcceptance={getAcceptance}
        acceptable={acceptable}
        accountNumber={accountNumber}
        openChanges={openChanges}
        acceptanceLoading={acceptanceLoading}
        agencyBill={false}
        environment={environment}
        sourceSystemCode={source_system_code}
        openCancellation={openCancellation}
        confirmClickHandler={confirmClickHandler}
        config={config}
        setIsUpdated={setIsUpdated}
        uploadToken={uploadToken}
        setStartReload={setStartReload}
        getRefundMethodClickHandler={getRefundMethodClickHandler}
        refundMethods={refundMethods}
        showCancelPolicyFlag={showCancelPolicyFlag?.enabled}
        isFarmRanchLine={isFarmRanchLine}
      />
    )}
    {showChangePolicyFlag?.enabled && (
      <ChangePolicy
        readOnlyAccess={false}
        readOnlyFeatureFlag={false}
        accountNumber={accountNumber}
        policyNumber={policyNumber}
        result={result}
        type={product}
        code={source_system_code}
        query_env={environment}
        uploadToken={uploadToken}
        openChanges={openChanges}
      />
    )}
    {
      isUpdated ? (
        <PolicyInfoBox
          accountNumber={accountNumber}
          policyNumber={policyNumber}
          environment={environment}
          sourceSystemCode={source_system_code}
        />
      ) : (
        <GpaPolicyInfo
          dataLoading={dataLoading}
          result={result} />
      )
    }
  </div>;
};
